import { createFileRoute } from "@tanstack/react-router";
import { useFormatter, useTranslations } from "next-intl";
import { PageHeader } from "@/components/page-header";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  FolderOpen,
  Inbox,
  Users,
  Car,
  Shield,
  FileText,
  AlertTriangle,
  Euro,
  Clock,
  CalendarClock,
} from "lucide-react";
import { getRequestStatusVariant, getAppointmentStatusVariant } from "@/lib/labels";
import { Link, useRouter } from "@/i18n/navigation";
import { UserMention } from "@/components/user-mention";
import { UserMentionById } from "@/components/user-mention-by-id";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useStatistics } from "@/hooks/use-statistics";
import { useLatestRequests } from "@/hooks/use-latest-requests";
import { useAppointmentRequests } from "@/hooks/use-appointment-requests";
import { TimeseriesSection } from "@/components/dashboard/timeseries-section";

export const Route = createFileRoute("/_dashboard/")({ component: DashboardHome });

function StatBar({
  label,
  value,
  total,
  color,
}: {
  label: string;
  value: number;
  total: number;
  color: string;
}) {
  const pct = total > 0 ? (value / total) * 100 : 0;
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-sm">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-medium">{value}</span>
      </div>
      <div
        className="bg-muted h-2 overflow-hidden rounded-full"
        role="progressbar"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={Math.max(total, value, 1)}
        aria-valuenow={value}
        aria-valuetext={`${value} / ${total}`}
      >
        <div
          className={`h-full rounded-full ${color}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function KPISkeleton() {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="size-4" />
      </CardHeader>
      <CardContent>
        <Skeleton className="h-8 w-16 mb-1" />
        <Skeleton className="h-3 w-32" />
      </CardContent>
    </Card>
  );
}

function BreakdownSkeleton() {
  return (
    <Card>
      <CardHeader>
        <Skeleton className="h-5 w-32" />
      </CardHeader>
      <CardContent className="space-y-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="space-y-1">
            <div className="flex items-center justify-between">
              <Skeleton className="h-4 w-20" />
              <Skeleton className="h-4 w-8" />
            </div>
            <Skeleton className="h-2 w-full rounded-full" />
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function LatestRequestsSkeleton() {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          {Array.from({ length: 4 }).map((_, i) => (
            <TableHead key={i}>
              <Skeleton className="h-4 w-20" />
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {Array.from({ length: 5 }).map((_, i) => (
          <TableRow key={i}>
            {Array.from({ length: 4 }).map((_, j) => (
              <TableCell key={j}>
                <Skeleton className="h-5 w-full" />
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function DashboardSectionError({
  message,
  isRetrying,
  onRetry,
}: {
  message: string;
  isRetrying: boolean;
  onRetry: () => void;
}) {
  const t = useTranslations("Dashboard");

  return (
    <div
      role="alert"
      className="flex min-h-28 flex-col items-center justify-center gap-3 p-4 text-center"
    >
      <p className="text-muted-foreground text-sm">{message}</p>
      <Button variant="outline" onClick={onRetry} disabled={isRetrying}>
        {isRetrying ? t("retrying") : t("retry")}
      </Button>
    </div>
  );
}

function DashboardHome() {
  const t = useTranslations("Dashboard");
  const tRequestStatus = useTranslations("RequestStatus");
  const tRequestType = useTranslations("RequestType");
  const tRequestsTable = useTranslations("RequestsTable");
  const format = useFormatter();

  const tAppointments = useTranslations("AppointmentsPage");
  const tAppointmentStatus = useTranslations("AppointmentStatus");
  const {
    data: s,
    isLoading: statsLoading,
    isFetching: statsFetching,
    isError: statsError,
    refetch: refetchStats,
  } = useStatistics();
  const router = useRouter();
  const {
    data: latestRequests,
    isLoading: requestsLoading,
    isFetching: requestsFetching,
    isError: requestsError,
    refetch: refetchRequests,
  } = useLatestRequests(5);
  const {
    appointments,
    isLoading: appointmentsLoading,
    isFetching: appointmentsFetching,
    error: appointmentsError,
    refetch: refetchAppointments,
  } = useAppointmentRequests({
    limit: 5,
    scope: "queue",
    suppressGlobalError: true,
  });
  const pendingAppointments = appointments.filter(
    (a) => a.status === "submitted"
  );
  return (
    <>
      <PageHeader title={t("title")} />
      <div className="flex flex-1 flex-col gap-4 p-4">
        <h1 className="sr-only">{t("title")}</h1>
        {/* KPI cards */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {statsError ? (
            <Card className="sm:col-span-2 lg:col-span-4">
              <CardContent>
                <DashboardSectionError
                  message={t("statisticsLoadError")}
                  isRetrying={statsFetching}
                  onRetry={() => void refetchStats()}
                />
              </CardContent>
            </Card>
          ) : statsLoading || !s ? (
            Array.from({ length: 4 }).map((_, i) => <KPISkeleton key={i} />)
          ) : (
            <>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">
                    {t("cases")}
                  </CardTitle>
                  <FolderOpen className="text-muted-foreground size-4" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{s.cases.total}</div>
                  <p className="text-muted-foreground text-xs">
                    {t("activeCount", { count: s.cases.by_status.active })}
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">
                    {t("requests")}
                  </CardTitle>
                  <FileText className="text-muted-foreground size-4" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{s.requests.total}</div>
                  <p className="text-muted-foreground text-xs">
                    {t("pendingCount", {
                      count: s.requests.by_status.submitted,
                    })}
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">
                    {t("accidents")}
                  </CardTitle>
                  <AlertTriangle className="text-muted-foreground size-4" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{s.accidents.total}</div>
                  <p className="text-muted-foreground text-xs">
                    {t("withInjuriesCount", {
                      count: s.accidents.with_injuries,
                    })}
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">
                    {t("finances")}
                  </CardTitle>
                  <Euro className="text-muted-foreground size-4" />
                </CardHeader>
                <CardContent>
                  <div className="text-xl font-bold text-green-600 dark:text-green-400">
                    {format.number(s.financials.total_refunds, {
                      style: "currency",
                      currency: s.financials.currency,
                    })}
                  </div>
                  <p className="text-muted-foreground text-xs">
                    {t("expensesLabel", {
                      amount: format.number(
                        s.financials.total_legal_expenses,
                        {
                          style: "currency",
                          currency: s.financials.currency,
                        }
                      ),
                    })}
                  </p>
                </CardContent>
              </Card>
            </>
          )}
        </div>

        {/* Timeseries Charts */}
        <TimeseriesSection />

        {/* Latest Requests */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Inbox className="size-4" />
                {t("latestRequests")}
              </CardTitle>
              <CardDescription>
                {t("latestRequestsDescription")}
              </CardDescription>
            </div>
            <Link
              href="/requests"
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              {t("viewAll")}
            </Link>
          </CardHeader>
          <CardContent>
            {requestsError ? (
              <DashboardSectionError
                message={t("latestRequestsLoadError")}
                isRetrying={requestsFetching}
                onRetry={() => void refetchRequests()}
              />
            ) : requestsLoading ? (
              <LatestRequestsSkeleton />
            ) : latestRequests.length === 0 ? (
              <p className="text-muted-foreground text-sm">
                {t("latestRequestsEmpty")}
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{tRequestsTable("type")}</TableHead>
                    <TableHead>{tRequestsTable("status")}</TableHead>
                    <TableHead>{tRequestsTable("requester")}</TableHead>
                    <TableHead>{tRequestsTable("createdAt")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {latestRequests.map((req) => {
                    const href = `/requests/${req.id}?type=${encodeURIComponent(req.type)}`;
                    const requesterName = [
                      req.requester_first_name,
                      req.requester_last_name,
                    ]
                      .filter(Boolean)
                      .join(" ");
                    const typeLabel = tRequestType.has(
                      req.type as "consultancy"
                    )
                      ? tRequestType(req.type as "consultancy")
                      : req.type;
                    const statusLabel = tRequestStatus.has(
                      req.status as "draft"
                    )
                      ? tRequestStatus(req.status as "draft")
                      : req.status;
                    return (
                      <TableRow
                        key={req.id}
                        className="cursor-pointer"
                        onClick={() => router.push(href)}
                      >
                        <TableCell>
                          <Link
                            href={href}
                            onClick={(event) => event.stopPropagation()}
                            className="inline-flex rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                          >
                            <Badge variant="outline">{typeLabel}</Badge>
                          </Link>
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={getRequestStatusVariant(req.status)}
                          >
                            {statusLabel}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <UserMention
                            userId={req.requester_id}
                            name={requesterName || req.requester_id.slice(0, 8)}
                            onClick={(event) => event.stopPropagation()}
                          />
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {format.dateTime(new Date(req.created_at), {
                            day: "2-digit",
                            month: "2-digit",
                            year: "numeric",
                          })}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Appointment requests */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <CalendarClock className="size-4" />
                {tAppointments("title")}
                {pendingAppointments.length > 0 && (
                  <Badge variant="info">{pendingAppointments.length}</Badge>
                )}
              </CardTitle>
              <CardDescription>
                {tAppointments("dashboardDescription")}
              </CardDescription>
            </div>
            <Link
              href="/appointments"
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              {t("viewAll")}
            </Link>
          </CardHeader>
          <CardContent>
            {appointmentsError ? (
              <DashboardSectionError
                message={t("appointmentsLoadError")}
                isRetrying={appointmentsFetching}
                onRetry={() => void refetchAppointments()}
              />
            ) : appointmentsLoading ? (
              <LatestRequestsSkeleton />
            ) : pendingAppointments.length === 0 ? (
              <p className="text-muted-foreground text-sm">
                {tAppointments("dashboardEmpty")}
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{tAppointments("requester")}</TableHead>
                    <TableHead>{tAppointments("preferredStart")}</TableHead>
                    <TableHead>{tRequestsTable("status")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pendingAppointments.map((appt) => {
                    const href = `/appointments?appointment=${appt.id}`;
                    return (
                      <TableRow
                        key={appt.id}
                        className="cursor-pointer"
                        onClick={() => router.push(href)}
                      >
                        <TableCell>
                          <UserMentionById
                            userId={appt.requester_id}
                            onClick={(event) => event.stopPropagation()}
                          />
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          <Link
                            href={href}
                            onClick={(event) => event.stopPropagation()}
                            className="rounded-sm hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                          >
                            {format.dateTime(
                              new Date(appt.preferred_start_at),
                              {
                                day: "2-digit",
                                month: "2-digit",
                                year: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              }
                            )}
                          </Link>
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={getAppointmentStatusVariant(appt.status)}
                          >
                            {tAppointmentStatus(appt.status)}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Breakdowns */}
        {!statsError && (
          <div className="grid gap-4 md:grid-cols-3">
            {statsLoading || !s ? (
              Array.from({ length: 3 }).map((_, i) => (
                <BreakdownSkeleton key={i} />
              ))
            ) : (
              <>
              {/* Cases by Status */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FolderOpen className="size-4" />
                    {t("casesByStatus")}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <StatBar
                    label={t("draft")}
                    value={s.cases.by_status.draft}
                    total={s.cases.total}
                    color="bg-gray-400"
                  />
                  <StatBar
                    label={t("active")}
                    value={s.cases.by_status.active}
                    total={s.cases.total}
                    color="bg-blue-500"
                  />
                  <StatBar
                    label={t("suspended")}
                    value={s.cases.by_status.suspended}
                    total={s.cases.total}
                    color="bg-amber-500"
                  />
                  <StatBar
                    label={t("closed")}
                    value={s.cases.by_status.closed}
                    total={s.cases.total}
                    color="bg-green-500"
                  />
                  <StatBar
                    label={t("cancelled")}
                    value={s.cases.by_status.cancelled}
                    total={s.cases.total}
                    color="bg-red-500"
                  />
                </CardContent>
              </Card>

              {/* Requests by Type */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="size-4" />
                    {t("requestsByType")}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <StatBar
                    label={t("consultancy")}
                    value={s.requests.by_type.consultancy}
                    total={s.requests.total}
                    color="bg-violet-500"
                  />
                  <StatBar
                    label={t("accidentSos")}
                    value={s.requests.by_type.accident_sos}
                    total={s.requests.total}
                    color="bg-orange-500"
                  />
                  <StatBar
                    label={t("accidentReport")}
                    value={s.requests.by_type.accident_report}
                    total={s.requests.total}
                    color="bg-cyan-500"
                  />
                  <div className="border-t pt-3 mt-3">
                    <p className="text-muted-foreground text-xs mb-2">
                      {t("byStatus")}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <Badge variant="outline">
                        {t("drafts", { count: s.requests.by_status.draft })}
                      </Badge>
                      <Badge variant="outline">
                        {t("submitted", {
                          count: s.requests.by_status.submitted,
                        })}
                      </Badge>
                      <Badge variant="default">
                        {t("accepted", {
                          count: s.requests.by_status.accepted,
                        })}
                      </Badge>
                      <Badge variant="secondary">
                        {t("completed", {
                          count: s.requests.by_status.completed,
                        })}
                      </Badge>
                      <Badge variant="destructive">
                        {t("rejected", {
                          count: s.requests.by_status.rejected,
                        })}
                      </Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Overview: Users + Vehicles + Policies */}
              <Card>
                <CardHeader>
                  <CardTitle>{t("overview")}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-start gap-3">
                    <Users className="text-muted-foreground mt-0.5 size-4 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">
                          {t("users")}
                        </span>
                        <span className="text-sm font-bold">
                          {s.users.total}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-1.5 mt-1">
                        <Badge variant="default" className="text-xs">
                          {t("activeUsers", {
                            count: s.users.by_status.active,
                          })}
                        </Badge>
                        <Badge variant="destructive" className="text-xs">
                          {t("suspendedUsers", {
                            count: s.users.by_status.suspended,
                          })}
                        </Badge>
                        {s.users.by_status.deleted > 0 && (
                          <Badge variant="outline" className="text-xs">
                            {t("deletedUsers", {
                              count: s.users.by_status.deleted,
                            })}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <Car className="text-muted-foreground size-4 shrink-0" />
                    <div className="flex items-center justify-between flex-1">
                      <span className="text-sm font-medium">
                        {t("vehicles")}
                      </span>
                      <span className="text-sm font-bold">
                        {s.vehicles.total}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <Shield className="text-muted-foreground mt-0.5 size-4 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">
                          {t("policies")}
                        </span>
                        <span className="text-sm font-bold">
                          {s.policies.total}
                        </span>
                      </div>
                      {s.policies.expiring_within_30_days > 0 && (
                        <p className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1 mt-1">
                          <Clock className="size-3" />
                          {t("policiesExpiring", {
                            count: s.policies.expiring_within_30_days,
                          })}
                        </p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
              </>
            )}
          </div>
        )}
      </div>
    </>
  );
}
